/**
 * EventCard (Plan 03-06).
 *
 * 5-variant event card per UI-SPEC §Component Inventory EventCard variants:
 *   - compact     — month-cell pill (18px tall, title only, truncated)
 *   - medium      — blokken i dagvisningens tidslinje (glass, #157)
 *   - full        — today-strip card with location + full detail
 *   - today-strip — compact row in today/EventsTile list
 *   - agenda      — raden i dagsagendaen (#157, Stell_Kalender): klokkeslett
 *                   til venstre, glasskort med fargestripe, tittel, sted og
 *                   «til HH:MM», og deltakerne som InitialAvatar-stabel (D2)
 *
 * `full` brukes av /i-dag og er ikke restylet ennå. `today-strip` er raden i
 * «I dag» på kjøkkenskjermen (Stell_Kjokken, #160): klokkeslett, fargestripe,
 * stor tittel og sted under.
 *
 * Composes EventColorStripe (4-px left edge) + AttendeeChips (avatar stack).
 * D-05: color_token is resolved server-side; card just renders the value.
 * D-06: no drag-and-drop; tap → edit via onClick.
 * T-CAL-30: title/description are React children (auto-escaped; no dangerouslySetInnerHTML).
 */
import { useTranslation } from 'react-i18next';
import { EventColorStripe, resolveStripeSlot } from '../../components/primitives/EventColorStripe';
import { AttendeeChips } from '../../components/primitives/AttendeeChips';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { InitialAvatarStack } from '../../components/primitives/InitialAvatar';
import { Icon } from '../../components/primitives';
import { formatEventTimeRange, formatTime } from '../../i18n/formatters';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';

export type EventCardVariant = 'compact' | 'medium' | 'full' | 'today-strip' | 'agenda';

/** En deltaker slik kalenderen viser den: navn og personfarge. */
export interface EventAttendee {
  name: string;
  colorSlot: ColorSlot;
}

export interface EventCardProps {
  variant: EventCardVariant;
  id: string;
  title: string;
  starts_at: string;
  ends_at?: string | null;
  all_day: boolean;
  location?: string | null;
  color_token: string;
  attendee_ids: string[];
  /**
   * Deltakerne med navn og farge (#157). Når de er med, viser `agenda` og
   * `medium` dem som InitialAvatar-stabel; ellers står AttendeeChips.
   */
  attendees?: readonly EventAttendee[];
  is_recurring?: boolean;
  has_reminder?: boolean;
  /** Optional member names keyed by ID for aria-label in AttendeeChips. */
  memberNames?: Record<string, string>;
  /** Optional member color slots keyed by ID for avatar rendering. */
  memberColorSlots?: Record<string, ColorSlot>;
  onClick?: () => void;
}

/** Stripen som avrundet strek inne i kortet, som skissen. */
function RoundStripe({ slot }: { slot: ReturnType<typeof resolveStripeSlot> }) {
  return (
    <span className="flex shrink-0 overflow-hidden rounded-pill">
      <EventColorStripe slot={slot} />
    </span>
  );
}

export function EventCard(props: EventCardProps) {
  const { t } = useTranslation();
  const {
    variant,
    title,
    starts_at,
    ends_at,
    all_day,
    location,
    color_token,
    attendee_ids,
    attendees,
    is_recurring,
    has_reminder,
    memberNames,
    memberColorSlots,
    onClick,
  } = props;

  const slot = resolveStripeSlot(color_token);
  const start = new Date(starts_at);
  const end = ends_at ? new Date(ends_at) : null;
  const timeLabel = formatEventTimeRange(start, end, all_day);

  if (variant === 'agenda') {
    // Sted og sluttid under tittelen: «Holmen svømmehall · til 11:00».
    const meta = [
      location,
      !all_day && end ? t('calendar.agenda.until', { time: formatTime(end) }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid="event-card-agenda"
        aria-label={t('calendar.eventCard.aria', { title, time: timeLabel })}
        className="flex w-full gap-3 text-left"
      >
        <span
          className={[
            'w-12 shrink-0 pt-3 tnum font-extrabold',
            all_day ? 'text-caption' : 'text-sm',
          ].join(' ')}
        >
          {all_day ? t('calendar.eventCard.allDay') : formatTime(start)}
        </span>
        <span
          className={`${GLASS_SURFACE} flex min-h-11 min-w-0 flex-1 gap-3 rounded-row py-3 pr-3.5 pl-3 shadow-md`}
        >
          <RoundStripe slot={slot} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-body font-extrabold">{title}</span>
            {meta ? (
              <span className="mt-0.5 block truncate text-caption text-ink-2">{meta}</span>
            ) : null}
          </span>
          {attendees && attendees.length > 0 ? (
            <InitialAvatarStack members={attendees} size={28} max={3} className="self-center" />
          ) : null}
        </span>
      </button>
    );
  }

  if (variant === 'today-strip') {
    // Ikke en knapp, og `onClick` brukes ikke: hele «I dag»-kortet på
    // kjøkkenet er knappen (åpner /i-dag), og en knapp inni en knapp er
    // nested-interactive (axe).
    return (
      <div
        role="group"
        data-testid="event-card-today-strip"
        aria-label={t('calendar.eventCard.aria', { title, time: timeLabel })}
        className="flex w-full gap-3.5 text-left"
      >
        <RoundStripe slot={slot} />
        <span
          className={[
            'w-16 shrink-0 tnum font-extrabold',
            all_day ? 'pt-1 text-body-s' : 'text-xl',
          ].join(' ')}
        >
          {all_day ? t('calendar.eventCard.allDay') : formatTime(start)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xl font-extrabold">{title}</span>
          {location ? (
            <span className="block truncate text-body text-ink-2">{location}</span>
          ) : null}
        </span>
        {attendee_ids.length > 0 ? (
          <AttendeeChips
            memberIds={attendee_ids}
            size="md"
            {...(memberNames ? { memberNames } : {})}
            {...(memberColorSlots ? { memberColorSlots } : {})}
          />
        ) : null}
      </div>
    );
  }

  // Per-variant outer classes
  const outerClass: Record<Exclude<EventCardVariant, 'agenda' | 'today-strip'>, string> = {
    compact:
      'flex items-center gap-1 h-4.5 overflow-hidden rounded-xs bg-inset pr-1 text-label font-bold text-ink',
    // Tett nok til at tittelen får plass i en halvtime (24 px) og tittel og tid i en time (48 px).
    medium: `${GLASS_SURFACE} relative flex h-full gap-2 overflow-hidden rounded-md px-2 py-1 shadow-md`,
    full: 'relative flex gap-3 bg-sheet rounded-lg p-3 shadow-sm',
  };

  const avatarSize = variant === 'compact' ? 'sm' : variant === 'medium' ? 'sm' : 'md';

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`event-card-${variant}`}
      aria-label={t('calendar.eventCard.aria', { title, time: timeLabel })}
      className={`text-left w-full ${outerClass[variant]}`}
    >
      {variant === 'medium' ? <RoundStripe slot={slot} /> : <EventColorStripe slot={slot} />}

      <div className={`flex-1 min-w-0 flex flex-col ${variant === 'medium' ? 'leading-tight' : 'gap-0.5'}`}>
        <span
          className={`truncate block ${
            variant === 'compact'
              ? 'text-label font-bold'
              : variant === 'medium'
                ? 'text-sm font-extrabold'
                : 'text-body-s font-semibold'
          }`}
        >
          {title}
        </span>

        {variant !== 'compact' && (
          <span className="text-ink-2 text-caption tnum">
            {timeLabel}
          </span>
        )}

        {variant === 'full' && location && (
          <span className="text-ink-2 text-label truncate">{location}</span>
        )}
      </div>

      {/* Recurring + reminder icons (medium + full only) */}
      {(variant === 'medium' || variant === 'full') && (
        <div className="flex items-center gap-1 shrink-0">
          {has_reminder && (
            <Icon.Bell size={14} className="text-ink-2" title={t('calendar.reminder.forSelf')} />
          )}
          {is_recurring && (
            <Icon.Repeat size={14} className="text-ink-2" />
          )}
        </div>
      )}

      {/* Attendees: InitialAvatar-stabel når navn og farge er med (#157), ellers AttendeeChips */}
      {variant === 'medium' && attendees && attendees.length > 0 ? (
        <InitialAvatarStack members={attendees} size={22} max={3} className="self-start" />
      ) : (
        variant !== 'compact' &&
        attendee_ids.length > 0 && (
          <AttendeeChips
            memberIds={attendee_ids}
            size={avatarSize}
            {...(memberNames ? { memberNames } : {})}
            {...(memberColorSlots ? { memberColorSlots } : {})}
          />
        )
      )}
    </button>
  );
}
